import type { RenderTextBox } from '@lite-workspace/shared/canvas-engine/core';
import type { BoxBevel, BoxGlow, BoxSatin, BoxPatternOverlay, EffectContourPoint } from '@lite-workspace/dto-fotoclass-templates';
// eslint-disable-next-line @nx/enforce-module-boundaries -- leaf constant; barrel pulls Angular/Konva, breaks this lib's vitest
import {
  FAUX_BOLD_STROKE_RATIO,
  PSD_FAUX_BOLD_TYPED_RATIO,
  PSD_FAUX_BOLD_SPREAD_STROKE_RATIO,
  PSD_AA_STRONG_WIDTH,
  PSD_AA_CRISP_MIN_PX,
  PSD_AA_CRISP_FULL_PX,
  PSD_AA_CRISP_MID_WIDTH,
  PSD_AA_CRISP_WIDTH,
  FAUX_BOLD_MIN_WEIGHT_GAP,
  FAUX_BOLD_FULL_WEIGHT_GAP,
  FAUX_BOLD_MIN_WIDTH,
} from '../../../canvas-engine/src/lib/svg/text-constants';
import { textMatrixDrawTransform, composeBoxDrawTransform, textMatrixScale, textMatrixAnisotropy } from './text-affine';
import { fauxBoldSpreads } from './faux-bold';
import { buildFill, type SceneVectorFill } from './vector-scene-adapter';
import { italicOf, namedInstanceOf, slantsWithoutItal, type FontVariation, type FvarInstances } from './font-instances';

export type { FontVariation } from './font-instances';

export interface LaidOutGlyph {
  fontRef: string;
  glyphId: number;
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  fontSize: number;
  /** Per-glyph tangent rotation (rad) for text-on-path; omitted/0 for axis-aligned text. */
  rotation?: number;
  /** Per-glyph fill override (hex #RRGGBB) for multi-run; Rust uses it instead of the box fill. */
  fill?: string;
  /**
   * Every line stretch of the layout in order, the same array on each glyph — set only when a stretch draws a line;
   * stays out of the scene.
   */
  decorations?: readonly GlyphDecoration[];
}

/**
 * One line stretch (a run's part of a line), flagged or not — a stretch of hidden tabs included. Positions are the
 * layout's book px.
 */
export interface GlyphDecoration {
  underline: boolean;
  strikethrough: boolean;
  /** Index of the text line the stretch sits on. */
  line: number;
  /** Pen span of the stretch, the last glyph's advance included. */
  x0: number;
  x1: number;
  baseline: number;
  /** The run's font size without the small-caps reduction. */
  fontSize: number;
  yUnderline: number;
  color: string;
}

// Mirror of the Rust adapter's `glyph_layout` decoration constants; render-rule.json `decoration` referees both.
export const DECORATION_THICKNESS_EM = 0.05;
export const UNDERLINE_OFFSET_EM = 0.15;
export const STRIKETHROUGH_OFFSET_EM = 0.3;
/** Thinnest line in scene px — floored after the render scale, as in Rust. */
export const DECORATION_MIN_THICKNESS = 1;

export interface SceneStroke {
  gradient?: import('@lite-workspace/dto-fotoclass-templates').BoxStroke['gradient'];
  overprint?: boolean;
  fillType?: 'color' | 'gradient' | 'pattern';
  patternRef?: string;
  patternScale?: number;
  patternLinked?: boolean;
  patternPhaseX?: number;
  patternPhaseY?: number;
  patternPhaseUnits?: import('@lite-workspace/dto-fotoclass-templates').BoxStroke['patternPhaseUnits'];
  blendMode?: string;
  color: string;
  width: number;
  opacity: number;
  position?: 'outside' | 'center' | 'inside';
}

export interface SceneDecoration {
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
}

export interface SceneShadow {
  angle?: number;
  distance?: number;
  noise?: number;
  antialiased?: boolean;
  layerConceals?: boolean;
  contour?: readonly EffectContourPoint[];
  color: string;
  blur: number;
  dx: number;
  dy: number;
  opacity: number;
  spread: number;
  blend: string;
}

export interface SceneTextBox {
  effectReferencePoint?: { x: number; y: number };
  effectDocumentOrigin?: { x: number; y: number };
  layerGlows?: BoxGlow[];
  satin?: BoxSatin;
  patternOverlay?: Omit<BoxPatternOverlay, 'pattern'> & { patternRef: string };
  colorOverlay?: { color: string; opacity?: number; enabled?: boolean; blendMode?: string };
  gradientOverlay?: Omit<import('@lite-workspace/dto-fotoclass-templates').BoxGradientOverlay, 'rendering' | 'colors'> & {
    colors?: string[];
  };
  /** Photoshop instances after `colorOverlay`/`gradientOverlay`, in list order: each is painted beneath the previous. */
  extraColorOverlays?: NonNullable<SceneTextBox['colorOverlay']>[];
  extraGradientOverlays?: NonNullable<SceneTextBox['gradientOverlay']>[];
  fillOpacity?: number;
  blendInteriorElements?: boolean;
  bevel?: Omit<BoxBevel, 'texture'> & {
    texture?: Pick<NonNullable<BoxBevel['texture']>, 'scale' | 'depth' | 'invert' | 'linked' | 'phaseX' | 'phaseY'> & {
      patternRef: string;
    };
  };
  type: 'text';
  id: string;
  rect: { x: number; y: number; width: number; height: number; rotation?: number };
  glyphs: LaidOutGlyph[];
  fill?: string;
  /** Legacy gradient fill in scene pixels; independent live overlays use `gradientOverlay` and its source frames. */
  fillGradient?: SceneVectorFill;
  strokes: SceneStroke[];
  decorations: SceneDecoration[];
  /** Все внешние тени слоя; первая в списке рисуется поверх остальных, как в Photoshop. */
  shadows?: SceneShadow[];
  /** Внутренние тени — клипуются по глифам и ложатся поверх заливки, под обводкой. */
  innerShadows?: SceneShadow[];
  /** Outer glows как zero-offset «тени» с blend'ом самого эффекта (PS-дефолт screen) — рендерятся над shadows, под глифами. */
  glows?: SceneShadow[];
  blend: string;
  opacity?: number;
  variation?: FontVariation;
  /** Axes of the runs drawn on an instance of their own, by the run's font key; a glyph whose key is absent takes
   *  `variation`. Mirrors scene.rs `TextBox::run_variations`. */
  runVariations?: Record<string, FontVariation>;
  /** Draw-time affine (tiny-skia from_row order) applying the editor's matrix/rotation; omitted = identity. */
  transform?: number[];
  /** Faux-bold thickening (px). Rust emboldens the fill via offset-fill; a contour stroke would outline counters. */
  fauxBoldWidth?: number;
  /** Photoshop anti-aliasing mode; Rust paints `none` aliased. Omitted = plain coverage. */
  antiAlias?: SceneTextAntiAlias;
  /** Photoshop Warp Text: Rust bends the glyph outlines inside this rect-normalized frame. */
  warp?: import('@lite-workspace/dto-fotoclass-templates').TextWarpParams;
  locked?: boolean;
  /** Glyph ink bbox ∪ rect, when glyphs overflow the frozen frame (point text) — region lifts copy THIS, not rect. */
  inkRect?: { x: number; y: number; width: number; height: number };
}

export type SceneTextAntiAlias = 'none' | 'sharp' | 'crisp' | 'strong' | 'smooth';

// Index = the model's `antiAlias` number (AgTextProperties.antiAlias). Mirrors text_anti_alias (adapter/mod.rs).
const SCENE_ANTI_ALIAS: readonly SceneTextAntiAlias[] = ['none', 'sharp', 'crisp', 'strong', 'smooth'];
const AA_STRONG = SCENE_ANTI_ALIAS.indexOf('strong');
const AA_CRISP = SCENE_ANTI_ALIAS.indexOf('crisp');

/**
 * The stroke (book px) a Photoshop anti-alias mode adds over plain coverage. `fontSize` is the box's own size in book
 * px: the typed size times the text matrix's nominal x scale (the parser multiplied it in), before renderScale; under
 * a non-uniform matrix the x scale decides. One rule per box — its runs' sizes do not take part. Compared in f32, as
 * the Rust box parses it, so a size within half an f32 ulp of a threshold steps the same in editor and print.
 * Mirrors anti_alias_width (adapter/mod.rs).
 */
function antiAliasWidth(antiAlias: number | undefined, fontSize: number): number {
  if (antiAlias === AA_STRONG) return PSD_AA_STRONG_WIDTH;
  if (antiAlias !== AA_CRISP) return 0;
  const size = Math.fround(fontSize);
  if (size >= PSD_AA_CRISP_FULL_PX) return PSD_AA_CRISP_WIDTH;
  return size >= PSD_AA_CRISP_MIN_PX ? PSD_AA_CRISP_MID_WIDTH : 0;
}

// Same rule as shared-font-cache.deriveVariationCoords — the layout font is variation-set in memory, but Rust gets
// the raw default-instance bytes, so we forward the axis values to apply there. render-rule.json pins both copies.
const WGHT_BY_LABEL: Readonly<Record<string, number>> = {
  Thin: 100,
  ExtraLight: 200,
  Light: 300,
  Regular: 400,
  Medium: 500,
  SemiBold: 600,
  Bold: 700,
  ExtraBold: 800,
  Black: 900,
};

/** The wght a text box carries when nothing chose one: the model's default weight label, not a request. */
export const DEFAULT_WGHT = WGHT_BY_LABEL['Regular'];

const WDTH_BY_LABEL: Readonly<Record<string, number>> = {
  UltraCondensed: 50,
  ExtraCondensed: 62.5,
  Condensed: 75,
  SemiCondensed: 87.5,
  Normal: 100,
  SemiExpanded: 112.5,
  Expanded: 125,
  ExtraExpanded: 150,
  UltraExpanded: 200,
};

const ASCII_ONLY = /^[ -~]*$/;

// Rust char::is_whitespace (Unicode White_Space): JS \s also splits on U+FEFF and keeps U+0085.
const RUST_WHITESPACE = /[\t\n\v\f\r \u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+/;

const WGHT_BY_FOLDED_LABEL: ReadonlyMap<string, number> = new Map(
  Object.entries(WGHT_BY_LABEL).map(([label, wght]) => [label.toLowerCase(), wght]),
);

const exactAxis = (table: Readonly<Record<string, number>>, token: string): number | undefined =>
  Object.prototype.hasOwnProperty.call(table, token) ? table[token] : undefined;

// Rust eq_ignore_ascii_case: a non-ASCII token never folds (toLowerCase turns the Kelvin sign into k).
const foldedWght = (token: string): number | undefined =>
  ASCII_ONLY.test(token) ? WGHT_BY_FOLDED_LABEL.get(token.toLowerCase()) : undefined;

// Mirrors derive_font_variation: first wght/wdth token wins; weight words also match in any ASCII case (saved
// `bold`), width words only exactly; the lone lower-case CSS `normal` is Regular while `Normal` stays the width word.
export function deriveFontVariation(fontWeight: string, italic: boolean): FontVariation | undefined {
  const tokens = (fontWeight ?? '').split(RUST_WHITESPACE).filter(Boolean);
  let wght: number | undefined;
  let wdth: number | undefined;
  for (const t of tokens) {
    const exactWght = wght === undefined ? exactAxis(WGHT_BY_LABEL, t) : undefined;
    if (exactWght !== undefined) {
      wght = exactWght;
      continue;
    }

    const exactWdth = wdth === undefined ? exactAxis(WDTH_BY_LABEL, t) : undefined;
    if (exactWdth !== undefined) {
      wdth = exactWdth;
      continue;
    }

    if (wght === undefined) wght = foldedWght(t);
  }

  if (wght === undefined && wdth === undefined && tokens.length === 1 && tokens[0] === 'normal') {
    wght = WGHT_BY_LABEL['Regular'];
  }

  const v: FontVariation = {};
  if (wght !== undefined) v['wght'] = wght;
  if (wdth !== undefined) v['wdth'] = wdth;
  if (italic) v['ital'] = 1;
  return Object.keys(v).length > 0 ? v : undefined;
}

/** The instance a font-mapping row names inside its file — the row's `targetPostScriptName` and `fontMetrics`. */
export interface MappedFace {
  targetPostScriptName?: string;
  fontMetrics?: { postScriptName?: string; weight?: string; style?: string };
}

const foldedLabel = (label: string | undefined): string => (label ?? '').trim().toLowerCase();

/** The weight label and slant a text shapes with when its mapping row names an instance of the file. */
export interface PinnedInstanceFace {
  fontWeight: string;
  italic: boolean;
}

// The instance a mapping row pins: its target names the instance its fontMetrics describe, and the instance's weight
// label yields a wght. The slant is the instance's or the text's, so a row never drops a text's italic. Whether the
// file has a variable wght axis is the caller's check. Mirrors pinned_instance_variation (adapter/mod.rs).
export function pinnedInstanceFace(mapped: MappedFace | undefined, italic: boolean): PinnedInstanceFace | undefined {
  const instance = mapped?.fontMetrics;
  const target = foldedLabel(mapped?.targetPostScriptName);
  if (!instance || !target || target !== foldedLabel(instance.postScriptName)) return undefined;
  const fontWeight = instance.weight ?? '';
  if (deriveFontVariation(fontWeight, false)?.['wght'] === undefined) return undefined;
  return { fontWeight, italic: italic || foldedLabel(instance.style) === 'italic' };
}

// A variable file draws the named instance its mapping row targets — or, with no target, the one whose PostScript name
// the text asks for — with every axis value the file gives it (font-instances.ts). With no such instance: a
// variable-wght file draws the instance its mapping row pins by weight label, even over a swapped family; otherwise
// the text's weight label, or the file's default instance under a swapped family. An italic text on a file that slants
// only by slnt takes the slant of the file's italic instance. Mirrors pinned_or_derived_variation (adapter/mod.rs).
export function pinnedOrDerivedVariation(
  fontWeight: string,
  italic: boolean,
  familySubstituted: boolean,
  variableWeight: boolean,
  mapped: MappedFace | undefined,
  requestedPostScriptName?: string,
  fvar?: FvarInstances,
): FontVariation | undefined {
  if (fvar) {
    const named = namedInstanceOf(fvar, mapped?.targetPostScriptName, requestedPostScriptName);
    if (named) return italic ? italicOf(fvar, { ...named.coordinates }) : { ...named.coordinates };
  }

  const pinned = variableWeight ? pinnedInstanceFace(mapped, italic) : undefined;
  const variation = pinned
    ? deriveFontVariation(pinned.fontWeight, pinned.italic)
    : familySubstituted
      ? undefined
      : deriveFontVariation(fontWeight, italic);

  return variation && fvar && italic && slantsWithoutItal(fvar) ? italicOf(fvar, variation) : variation;
}

export interface AdapterDeps {
  layoutGlyphs: (box: RenderTextBox) => LaidOutGlyph[];
  /** Actual usWeightClass of the loaded font file — used to synthesize bold when the file lacks it. */
  usWeightClass?: (box: RenderTextBox) => number;
  /** True when the font exposes a variable wght axis — the axis renders the weight, so faux-bold must not stack on top. */
  hasVariableWeight?: (box: RenderTextBox) => boolean;
  /** True when a font MAPPING swapped the family (e.g. "Inter" → an Alumni Sans file) — the mapped file's own
   *  look is the intent, so no weight-label synthesis/variation on top (Konva parity). */
  familySubstituted?: (box: RenderTextBox) => boolean;
  /** The instance the font-mapping row behind this box's face names — pins the variation on a variable file. */
  mappedFace?: (box: RenderTextBox) => MappedFace | undefined;
  /** The named instances of the box's variable font file (`fvar`); absent for a static file. */
  fvarInstances?: (box: RenderTextBox) => FvarInstances | undefined;
  /** The runs on a font of their own, with the axes their glyphs are laid out at: one per font key, none on the box's
   *  key (glyph_layout.rs `run_font_variations`). Absent: every run draws at the box axes. */
  runFaceVariations?: (box: RenderTextBox) => ReadonlyArray<RunFaceVariation>;
}

/** A run's font key and the axes it is laid out at; `undefined` — a face with no axes to set. */
export interface RunFaceVariation {
  fontRef: string;
  variation: FontVariation | undefined;
}

const sameVariation = (a: FontVariation | undefined, b: FontVariation | undefined): boolean =>
  a === b || (!!a && !!b && Object.keys(a).length === Object.keys(b).length && Object.keys(a).every(tag => a[tag] === b[tag]));

/**
 * The scene's per-run axes: a run whose axes differ from the box's gets its own entry (a face with none gets `{}`),
 * the first run on a key wins, and nothing to add leaves no map. Mirrors adapter/mod.rs `variation_of`.
 */
export function runVariationsOf(
  runs: ReadonlyArray<RunFaceVariation>,
  boxVariation: FontVariation | undefined,
): Record<string, FontVariation> | undefined {
  const out: Record<string, FontVariation> = {};
  const seen = new Set<string>();
  for (const run of runs) {
    if (seen.has(run.fontRef)) continue;
    seen.add(run.fontRef);
    if (!sameVariation(run.variation, boxVariation)) out[run.fontRef] = run.variation ?? {};
  }

  return Object.keys(out).length > 0 ? out : undefined;
}

// Unicode-aware (matches Rust's char::is_alphanumeric() in glyph_layout.rs norm_font_name) — an ASCII/Russian-only
// charset used to strip other letters (Ukrainian і/ї/є/ґ, accented Latin), letting unrelated names falsely collide.
const normFontName = (s: string): string => s.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

/**
 * True when the resolved font entry belongs to the requested family (same family, any weight/style).
 * False → the font MAPPING swapped the family (e.g. "Inter" → an Alumni Sans file): the mapped file's
 * own look is the design intent, so weight-label synthesis/variation must not restyle it (Konva parity).
 * Conservative: unknown names on either side → related (keeps the Tahoma-Bold synthesis behavior).
 */
export function fontFamiliesRelated(
  requested: ReadonlyArray<string | undefined>,
  actual: ReadonlyArray<string | undefined>,
): boolean {
  const req = requested
    .filter((s): s is string => !!s)
    .map(normFontName)
    .filter(Boolean);
  const act = actual
    .filter((s): s is string => !!s)
    .map(normFontName)
    .filter(Boolean);
  if (req.length === 0 || act.length === 0) return true;
  return act.some(a => req.some(r => a.includes(r) || r.includes(a)));
}

/**
 * Faux-bold embolden width when the requested weight is heavier than the loaded font file actually
 * provides (e.g. a "Tahoma Bold" text mapped to a Regular Tahoma file). 0 when the file already
 * covers the weight, so real bold faces are never double-bolded, and 0 at or below `Regular` —
 * that label is a box's default when its PSD named no weight, not a request to thicken the face.
 */
export function syntheticBoldWidth(fontWeight: string, italic: boolean, usWeightClass: number, fontSize: number): number {
  const requested = deriveFontVariation(fontWeight, italic)?.['wght'];
  if (requested == null || requested <= DEFAULT_WGHT || requested - usWeightClass < FAUX_BOLD_MIN_WEIGHT_GAP) return 0;
  return Math.max(
    FAUX_BOLD_MIN_WIDTH,
    fontSize * FAUX_BOLD_STROKE_RATIO * Math.min(1, (requested - usWeightClass) / FAUX_BOLD_FULL_WEIGHT_GAP),
  );
}

function textTransform(box: RenderTextBox, glyphs: LaidOutGlyph[]): number[] | undefined {
  // Матрица отрисовки делит анизотропию матрицы, чтобы не применить её дважды: она сидит и в масштабе
  // глифа, и в самой матрице. Растяжение символов из PSD (`horizontalScale`, у обложек 139%) в матрице
  // не сидит и делиться не должно — иначе надпись рисуется натуральной шириной вместо задуманной.
  //
  // Делитель берём ИЗ МАТРИЦЫ, а не из `glyphs[0]`: у многорановой строки нулевой глиф несёт растяжение
  // СВОЕГО рана, и остальные раны получали чужую поправку целиком. Ветки — как в движке
  // (`layout_text_glyphs`): текст по контуру кладётся без анизотропии, у пустой раскладки делить нечего.
  const laidOutOnPath = !!box.textPath?.d;
  const [sx, sy] = glyphs.length === 0 || laidOutOnPath ? ([1, 1] as const) : textMatrixAnisotropy(box.transform);
  const boxX = Math.round(box.rect.x);
  const boxY = Math.round(box.rect.y);

  const matrix = box.transform ? textMatrixDrawTransform(box.transform, boxX, boxY, sx, sy) : undefined;
  // Interactive box.rotation composes on top of the PSD matrix, pivoting on the drawn quad's visual centre.
  return composeBoxDrawTransform(matrix, box.rect.rotation ?? 0, boxX + box.rect.width / 2, boxY + box.rect.height / 2);
}

export type SceneLayerStyles = Pick<
  SceneTextBox,
  | 'shadows'
  | 'innerShadows'
  | 'layerGlows'
  | 'satin'
  | 'patternOverlay'
  | 'bevel'
  | 'colorOverlay'
  | 'gradientOverlay'
  | 'extraColorOverlays'
  | 'extraGradientOverlays'
> & {
  strokes?: SceneStroke[];
};

export function toSceneLayerStyles(box: Pick<RenderTextBox, 'id' | 'effects'>): SceneLayerStyles {
  const strokes: SceneStroke[] = (box.effects?.strokes ?? []).flatMap((s, index) =>
    s.enabled === false
      ? []
      : [
          {
            color: s.color,
            width: s.width,
            opacity: s.opacity ?? 1,
            position: s.position,
            blendMode: s.blendMode,
            fillType: s.fillType,
            gradient: s.gradient,
            overprint: s.overprint,
            ...(s.fillType === 'pattern' && {
              patternRef: `${box.id}__stroke_pattern_${index}`,
              patternScale: s.patternScale,
              patternLinked: s.patternLinked,
              patternPhaseUnits: s.patternPhaseUnits,
              patternPhaseX: s.patternPhaseX,
              patternPhaseY: s.patternPhaseY,
            }),
          },
        ],
  );

  const pickShadows = (inner: boolean) =>
    (box.effects?.shadows ?? [])
      .filter(s => !!s.inner === inner && s.enabled !== false)
      .map(s => ({
        color: s.color,
        blur: s.blur,
        dx: s.offsetX,
        dy: s.offsetY,
        opacity: s.opacity ?? 1,
        spread: s.spread ?? 0,
        blend: s.blendMode ?? 'multiply',
        angle: s.angle,
        distance: s.distance,
        noise: s.noise,
        antialiased: s.antialiased,
        layerConceals: s.layerConceals,
        contour: s.contour,
      }));
  const shadows = pickShadows(false);
  const innerShadows = pickShadows(true);
  const { pattern: _pattern, ...patternOverlay } = box.effects?.patternOverlay ?? {};
  const bevel = box.effects?.bevel;
  const texture = bevel?.texture;
  let colorOverlay: SceneTextBox['colorOverlay'];
  let gradientOverlay: SceneTextBox['gradientOverlay'];
  if (
    box.effects?.gradientOverlay?.rendering === 'live' &&
    box.effects.gradientOverlay.enabled !== false &&
    box.effects.gradientOverlay.opacity !== 0
  ) {
    const { rendering: _rendering, colors, ...fields } = box.effects.gradientOverlay;
    gradientOverlay = { ...fields, ...(colors?.length ? { colors } : {}) };
  }

  if (box.effects?.colorOverlay?.rendering === 'live') {
    const { rendering: _rendering, ...fields } = box.effects.colorOverlay;
    colorOverlay = fields;
  }

  const extraColorOverlays = box.effects?.extraColorOverlays?.flatMap(({ rendering, ...fields }) =>
    rendering === 'live' ? [fields] : [],
  );
  const extraGradientOverlays = box.effects?.extraGradientOverlays?.flatMap(({ rendering, colors, ...fields }) =>
    rendering === 'live' && fields.enabled !== false && fields.opacity !== 0
      ? [{ ...fields, ...(colors?.length ? { colors } : {}) }]
      : [],
  );

  return {
    strokes: strokes.length ? strokes : undefined,
    shadows: shadows.length > 0 ? shadows : undefined,
    innerShadows: innerShadows.length > 0 ? innerShadows : undefined,
    layerGlows: box.effects?.glows,
    satin: box.effects?.satin,
    patternOverlay: box.effects?.patternOverlay ? { ...patternOverlay, patternRef: `${box.id}__pattern` } : undefined,
    bevel: bevel
      ? {
          ...bevel,
          texture: texture
            ? {
                patternRef: `${box.id}__bevel_texture`,
                scale: texture.scale,
                depth: texture.depth,
                invert: texture.invert,
                linked: texture.linked,
                phaseX: texture.phaseX,
                phaseY: texture.phaseY,
              }
            : undefined,
        }
      : undefined,
    colorOverlay,
    gradientOverlay,
    ...(extraColorOverlays?.length && { extraColorOverlays }),
    ...(extraGradientOverlays?.length && { extraGradientOverlays }),
  };
}

/** `renderScale` — the scale the scene applies to this book-px drawable afterwards; it only floors decoration lines. */
export function toTextSceneBox(box: RenderTextBox, deps: AdapterDeps, renderScale = 1): SceneTextBox {
  const glyphs = deps.layoutGlyphs(box);
  const styles = toSceneLayerStyles(box);
  const substituted = deps.familySubstituted?.(box) ?? false;
  const typedFontSize = box.fontSize / (box.transform ? (textMatrixScale(box.transform) ?? 1) : 1);
  const explicitBold = !box.fauxBold
    ? 0
    : fauxBoldSpreads(box)
      ? Math.min(typedFontSize, box.fontSize) * PSD_FAUX_BOLD_SPREAD_STROKE_RATIO
      : typedFontSize * PSD_FAUX_BOLD_TYPED_RATIO;
  const synthBold =
    deps.usWeightClass && !deps.hasVariableWeight?.(box) && !substituted
      ? syntheticBoldWidth(box.fontWeight, !!box.italic, deps.usWeightClass(box), box.fontSize)
      : 0;
  const boldWidth = Math.max(explicitBold, synthBold);
  const aaWidth = antiAliasWidth(box.antiAlias, box.fontSize);
  const width = (boldWidth > 0 ? Math.max(FAUX_BOLD_MIN_WIDTH, boldWidth) : 0) + aaWidth;
  const fauxBoldWidth = width > 0 && !box.strokeFlag ? width : undefined;
  const variation = pinnedOrDerivedVariation(
    box.fontWeight,
    !!box.italic,
    substituted,
    deps.hasVariableWeight?.(box) ?? false,
    deps.mappedFace?.(box),
    box.postScriptName,
    deps.fvarInstances?.(box),
  );
  const runVariations = deps.runFaceVariations && runVariationsOf(deps.runFaceVariations(box), variation);
  return {
    ...styles,
    ...(box.effectReferencePoint && { effectReferencePoint: box.effectReferencePoint }),
    ...(box.effectDocumentOrigin && { effectDocumentOrigin: box.effectDocumentOrigin }),
    type: 'text',
    id: box.id,
    rect: box.rect,
    inkRect: glyphInkRect(box.rect, glyphs),
    glyphs: glyphs.some(g => g.decorations) ? glyphs.map(({ decorations: _decorations, ...g }) => g) : glyphs,
    fill: box.fillFlag === false ? undefined : box.color,
    fillGradient:
      box.fillFlag === false ? undefined : buildFill(box.fillGradient, box.rect.x, box.rect.y, box.rect.width, box.rect.height),
    strokes: styles.strokes ?? [],
    decorations: buildDecorations(box, glyphs, DECORATION_MIN_THICKNESS / (renderScale > 0 ? renderScale : 1)),
    fillOpacity: box.fillOpacity,
    blendInteriorElements: box.blendInteriorElements,
    blend: box.blendMode ?? 'normal',
    opacity: box.opacity,
    variation,
    ...(runVariations && { runVariations }),
    transform: textTransform(box, glyphs),
    fauxBoldWidth,
    ...(box.antiAlias != null && SCENE_ANTI_ALIAS[box.antiAlias] && { antiAlias: SCENE_ANTI_ALIAS[box.antiAlias] }),
    ...(box.warp && { warp: box.warp }),
  };
}

// Free (point) text has no wrap frame: its glyphs legally overflow the frozen import rect (both sides for
// right/center align). Region-scoped lifts sized to rect would clip those glyphs mid-edit/drag, so the
// drawable carries the padded glyph bbox ∪ rect. Pads over-estimate advances/ascent — over-copy is harmless.
function glyphInkRect(
  rect: { x: number; y: number; width: number; height: number },
  glyphs: LaidOutGlyph[],
): { x: number; y: number; width: number; height: number } | undefined {
  if (glyphs.length === 0) return undefined;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const g of glyphs) {
    const emX = g.fontSize * g.scaleX;
    const emY = g.fontSize * g.scaleY;
    if (g.x - 0.25 * emX < minX) minX = g.x - 0.25 * emX;
    if (g.x + 1.25 * emX > maxX) maxX = g.x + 1.25 * emX;
    if (g.y - 1.1 * emY < minY) minY = g.y - 1.1 * emY;
    if (g.y + 0.45 * emY > maxY) maxY = g.y + 0.45 * emY;
  }

  const x = Math.min(rect.x, minX);
  const y = Math.min(rect.y, minY);
  const width = Math.max(rect.x + rect.width, maxX) - x;
  const height = Math.max(rect.y + rect.height, maxY) - y;
  const overflows =
    x < rect.x - 1 || y < rect.y - 1 || x + width > rect.x + rect.width + 1 || y + height > rect.y + rect.height + 1;
  return overflows ? { x, y, width, height } : undefined;
}

/** `minThick` — the 1 scene px floor in book px (divided by the render scale), so it holds after the scene scales. */
function buildDecorations(box: RenderTextBox, glyphs: LaidOutGlyph[], minThick: number): SceneDecoration[] {
  // A warp bends the glyphs but not a straight line: a warped box draws none, as in Rust.
  if (glyphs.length === 0 || box.warp) return [];
  const stretches = glyphs.find(g => g.decorations)?.decorations;
  if (stretches) return stretchDecorations(stretches, minThick);
  if (!box.underline && !box.strikethrough) return [];

  // Path and vertical layouts mark no stretches: they keep the one box-wide line.
  const minX = Math.min(...glyphs.map(g => g.x));
  const maxX = Math.max(...glyphs.map(g => g.x));
  const baseY = glyphs[0].y;
  const yUnderlineMultiplier = box.yUnderline ?? 1;
  const thick = Math.max(minThick, box.fontSize * DECORATION_THICKNESS_EM * yUnderlineMultiplier);
  const out: SceneDecoration[] = [];

  if (box.underline) {
    out.push({
      x: minX,
      y: baseY + box.fontSize * UNDERLINE_OFFSET_EM,
      width: maxX - minX,
      height: thick,
      color: box.color,
    });
  }

  if (box.strikethrough) {
    out.push({
      x: minX,
      y: baseY - box.fontSize * STRIKETHROUGH_OFFSET_EM,
      width: maxX - minX,
      height: thick,
      color: box.color,
    });
  }

  return out;
}

/**
 * A line per decorated stretch, on its own line's baseline at its run's size and colour; the next stretch of the same
 * line with the same line extends it, any other stretch (a plain run, a hidden tab) ends it. Mirror of the Rust
 * adapter's `DecorationLines`.
 */
function stretchDecorations(stretches: readonly GlyphDecoration[], minThick: number): SceneDecoration[] {
  const out: SceneDecoration[] = [];
  const open: [SceneDecoration | undefined, SceneDecoration | undefined] = [undefined, undefined];
  let line: number | undefined;
  for (const d of stretches) {
    if (d.line !== line) {
      line = d.line;
      open[0] = undefined;
      open[1] = undefined;
    }

    const thick = Math.max(minThick, d.fontSize * DECORATION_THICKNESS_EM * d.yUnderline);
    const kinds: [boolean, number][] = [
      [d.underline, d.baseline + d.fontSize * UNDERLINE_OFFSET_EM],
      [d.strikethrough, d.baseline - d.fontSize * STRIKETHROUGH_OFFSET_EM],
    ];
    kinds.forEach(([on, y], k) => {
      if (!on) {
        open[k] = undefined;
        return;
      }

      if (d.x1 <= d.x0) return;
      const prev = open[k];
      if (prev && Math.abs(prev.y - y) < 1e-3 && Math.abs(prev.height - thick) < 1e-3 && prev.color === d.color) {
        prev.width = Math.max(d.x1, prev.x + prev.width) - prev.x;
        return;
      }

      const rect = { x: d.x0, y, width: d.x1 - d.x0, height: thick, color: d.color };
      open[k] = rect;
      out.push(rect);
    });
  }

  return out;
}
